/* eslint-disable no-import-assign */
/* eslint-disable no-await-in-loop */
/* eslint-disable max-len */

/**
 * We are using symmetric encryption with single password
 * https://github.com.mcas.ms/openpgpjs/openpgpjs#browser-webpack
 */

import * as openpgp from 'openpgp';
import * as streamSaver from 'streamsaver';

streamSaver.mitm = '/mitm.html';

/**
 * This method is used to decrypt text with a key.
 * @param {string} ciphertext
 * @param {string} salt
 * @returns {Promise<string>} Decrypted string
 */
export const decryptTextByKey = async (ciphertext, salt) => {
    const encryptedMessage = await openpgp.readMessage({ armoredMessage: ciphertext });
    const { data: decrypted } = await openpgp.decrypt({ message: encryptedMessage, passwords: [salt] });
    return decrypted;
};

/**
 * This method is used to encrypt text.
 * @param {string} data
 * @returns {Promise<string>} Encrypted string
 */
export const encryptText = async data => {
    try {
        const sToken = localStorage.getItem('sToken');
        const userId = localStorage.getItem('userId');
        
        if (!sToken || !userId) {
            console.error('[ENCRYPT-TEXT] Missing sToken or userId');
            throw new Error('Missing sToken or userId in localStorage');
        }
        
        const salt = await decryptTextByKey(sToken, userId);
        const message = await openpgp.createMessage({ text: data });
        const encrypted = await openpgp.encrypt({ message, passwords: [salt] });
        
        return encrypted;
    } catch (error) {
        console.error('[ENCRYPT-TEXT] Encryption failed:', error.message);
        throw error;
    }
};

/**
 * Decrypts an armored ciphertext using a derived password from localStorage.
 * @param {string} ciphertext - The encrypted text to decrypt.
 * @returns {Promise<string>} - The decrypted string.
 */
export const decryptText = async ciphertext => {
    const sToken = localStorage.getItem('sToken');
    const userId = localStorage.getItem('userId');
    const salt = (sToken && userId) ? await decryptTextByKey(sToken, userId) : '';
    const encryptedMessage = await openpgp.readMessage({ armoredMessage: ciphertext });
    const { data: decrypted } = await openpgp.decrypt({ message: encryptedMessage, passwords: [salt] });
    return decrypted;
};

/**
 * This method is used to encrypt json object, array json
 * @param {object} data
 * @returns {string} encrypted string
 */
export const encryptJson = async data => {
    const sToken = localStorage.getItem('sToken');
    const userId = localStorage.getItem('userId');
    const salt = (sToken && userId) ? await decryptTextByKey(sToken, userId) : '';
    const message = await openpgp.createMessage({ text: JSON.stringify(data) });
    const encrypted = await openpgp.encrypt({ message, passwords: [salt] });
    return encrypted;
};

/**
 * This method is used to decrypt json object, array json
 * @param {string} ciphertext
 * @returns {string} decrypted string
 */
export const decryptJson = async ciphertext => {
    const sToken = localStorage.getItem('sToken');
    const userId = localStorage.getItem('userId');
    const salt = (sToken && userId) ? await decryptTextByKey(sToken, userId) : '';
    const encryptedMessage = await openpgp.readMessage({ armoredMessage: ciphertext });
    const { data: decrypted } = await openpgp.decrypt({ message: encryptedMessage, passwords: [salt] });
    return JSON.parse(decrypted);
};

/**
 * This method is used to conver file into Uint8Array and to encrypt it
 * @param {file} file
 * @returns {object} file info
 */
export const encryptUint8ArrayFile = async file => {
    const sToken = localStorage.getItem('sToken');
    const userId = localStorage.getItem('userId');
    const salt = (sToken && userId) ? await decryptTextByKey(sToken, userId) : '';
    const CHUNK_SIZE = 500 * 1024 * 1024; // 500 MB
    const chunks = [];
    for (let i = 0; i < file.size; i += CHUNK_SIZE) {
        const chunk = file.slice(i, i + CHUNK_SIZE);
        const responseArrayBuffer = await chunk.arrayBuffer();
        const message = await openpgp.createMessage({ binary: new Uint8Array(responseArrayBuffer) });
        const ciphertext = await openpgp.encrypt({ message, passwords: [salt], format: 'binary' });
        chunks.push(ciphertext);
    }
    return new Blob(chunks, { type: 'application/octet-stream' });
};

/**
 * This method is used to decrypt a large file in chunks.
 * @param {File} encryptedFile
 * @param {string} [sToken='']
 * @returns {Promise<Blob>} Decrypted file as a Blob
 */
export const decryptFile = async (encryptedFile, sToken = '') => {
    let salt;
    if (sToken) {
        salt = await decryptText(sToken);
    } else {
        const token = localStorage.getItem('sToken');
        const userId = localStorage.getItem('userId');
        salt = (token && userId) ? await decryptTextByKey(token, userId) : '';
    }
    const CHUNK_SIZE = 500 * 1024 * 1024; // 500 MB
    const chunks = [];
    for (let i = 0; i < encryptedFile.size; i += CHUNK_SIZE) {
        const chunk = encryptedFile.slice(i, i + CHUNK_SIZE);
        const responseArrayBuffer = await chunk.arrayBuffer();
        const encryptedMessage = await openpgp.readMessage({ binaryMessage: new Uint8Array(responseArrayBuffer) });
        const { data: decrypted } = await openpgp.decrypt({ message: encryptedMessage, passwords: [salt], format: 'binary' });
        chunks.push(decrypted);
    }
    return new Blob(chunks, { type: 'application/octet-stream' });
};

/**
 * This method is used to decrypt an entire file at once.
 * @param {Uint8Array} encryptedFile
 * @param {string} [sToken='']
 * @returns {Promise<Uint8Array>} Decrypted binary data
 */
export const decryptFileBulk = async (encryptedFile, sToken = '') => {
    let salt;
    if (sToken) {
        salt = await decryptText(sToken);
    } else {
        const token = localStorage.getItem('sToken');
        const userId = localStorage.getItem('userId');
        salt = (token && userId) ? await decryptTextByKey(token, userId) : '';
    }
    const encryptedMessage = await openpgp.readMessage({ binaryMessage: encryptedFile });
    const { data: decrypted } = await openpgp.decrypt({ message: encryptedMessage, passwords: [salt], format: 'binary' });
    return decrypted;
};

/**
 * This method is used to encrypt stream
 * @param {Stream} readableStream
 * @returns {String} encrypted
 */
export const encryptStream = async readableStream => {
    const sToken = localStorage.getItem('sToken');
    const userId = localStorage.getItem('userId');
    const salt = (sToken && userId) ? await decryptTextByKey(sToken, userId) : '';

    const message = await openpgp.createMessage({ binary: readableStream });
    const encrypted = await openpgp.encrypt({
        message,
        passwords: [salt],
        format: 'binary',
        config: { preferredCompressionAlgorithm: openpgp.enums.compression.zlib }
    });

    return encrypted;
};

/**
 * This method decrypts a stream and downloads the resulting file.
 * @param {ReadableStream} readableStream
 * @param {string} fileName
 * @param {string} [sToken='']
 * @returns {Promise<void>}
 */
export const decryptStreamAndDownload = async (readableStream, fileName, sToken = '') => {
    try {
        let salt;
        if (sToken) {
            salt = await decryptText(sToken);
        } else {
            const token = localStorage.getItem('sToken');
            const userId = localStorage.getItem('userId');
            salt = (token && userId) ? await decryptTextByKey(token, userId) : '';
        }

        const message = await openpgp.readMessage({ binaryMessage: readableStream });

        const decrypted = await openpgp.decrypt({
            message,
            passwords: [salt],
            format: 'binary',
            config: { allowUnauthenticatedStream: true, preferredCompressionAlgorithm: openpgp.enums.compression.zlib }
        });

        const writableStream = streamSaver.createWriteStream(fileName);
        decrypted.data.pipeTo(writableStream).then(() => console.log('Done writing decrypted file ...'));
    } catch (error) {
        console.error('Error during decryption:', error);
    }
};